import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Camera, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';

/**
 * Mini-onboarding utente INVITATO (post-accettazione invito), step 1 di 2:
 * foto profilo. Nome/cognome sono già stati raccolti dal form di accettazione,
 * qui chiediamo solo l'immagine (self-service → user_profiles.profile_image_url,
 * MAI avatar_url che è riservato agli admin). Interamente skippabile.
 */
export function WelcomeProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      if (!session?.user) {
        navigate('/login', { replace: true });
        return;
      }
      setUserId(session.user.id);
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, profile_image_url')
        .eq('id', session.user.id)
        .single();
      if (cancelled) return;
      if (profile?.full_name) setFirstName(profile.full_name.split(' ')[0]);
      if (profile?.profile_image_url) setImageUrl(profile.profile_image_url);
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Formato non supportato. Usa JPG o PNG.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('File troppo grande. Massimo 2MB.');
      return;
    }
    setUploadingImage(true);
    setError(null);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `profile_${userId}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(`profile/${fileName}`, file, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('uploads').getPublicUrl(`profile/${fileName}`);
      setImageUrl(publicUrl);
    } catch (err) {
      console.error('[WelcomeProfile] upload error:', err);
      setError("Impossibile caricare l'immagine");
    } finally {
      setUploadingImage(false);
    }
  };

  const goNext = () => navigate('/welcome/modules');

  const handleContinue = async () => {
    if (!userId || !imageUrl) { goNext(); return; }
    setIsSubmitting(true);
    setError(null);
    try {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ profile_image_url: imageUrl, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (profileError) throw profileError;
      await supabase.auth.updateUser({ data: { profile_image_url: imageUrl } });
      goNext();
    } catch (err) {
      console.error('[WelcomeProfile] save error:', err);
      setError('Errore durante il salvataggio. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
      </div>
    );
  }

  return (
    <RegisterSplitLayout visual={{ startCard: 'portal' }} step={1} totalSteps={2}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">
          {firstName ? `Benvenuto, ${firstName}` : 'Benvenuto in Flusia'}
        </h1>
        <p className="text-[#787774] text-sm">Aggiungi una foto profilo: il team ti riconosce al volo</p>
      </div>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingImage}
          className="relative w-20 h-20 rounded-full bg-[rgba(55,53,47,0.06)] border border-[rgba(55,53,47,0.16)] flex items-center justify-center overflow-hidden hover:border-[#2383E2] transition-colors"
        >
          {imageUrl ? (
            <img src={imageUrl} alt="Foto profilo" className="w-full h-full object-cover" />
          ) : uploadingImage ? (
            <Loader2 className="w-5 h-5 animate-spin text-[#787774]" />
          ) : (
            <Camera className="w-6 h-6 text-[#787774]" />
          )}
        </button>
        <div>
          <p className="text-sm font-medium text-[#37352F]">Foto profilo</p>
          <p className="text-xs text-[#787774]">JPG o PNG, massimo 2MB</p>
        </div>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />
      </div>
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
      <div className="mt-8 space-y-3">
        <button type="button" onClick={handleContinue} disabled={isSubmitting || uploadingImage} className="notion-register-btn w-full">
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              <span>Salvataggio...</span>
            </>
          ) : (
            <>
              <span>Continua</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
        <button type="button" onClick={goNext}
          className="w-full text-center text-sm text-[#787774] hover:text-[#37352F] transition-colors py-1.5">
          Salta
        </button>
      </div>
    </RegisterSplitLayout>
  );
}
